import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/useToast';
import { usePrompt } from '@/components/ui/usePrompt';
import { PartyPopper, Check, Ban, RefreshCw } from '@/components/icons';
import {
  approveCreditOverride,
  blockCredit,
  creditQueue,
  type DispatchOrder,
} from '@/api/dispatchOrder';
import { formatDate, formatINR, formatINRSymbol } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { orderRef } from '@/lib/orderRef';

const CREDIT_TONE = { GREEN: 'success', AMBER: 'warning', RED: 'danger' } as const;

export function CreditCheckPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const ask = usePrompt();

  const query = useQuery({
    queryKey: ['credit-queue'],
    queryFn: creditQueue,
    refetchInterval: 60_000,
  });
  const data = query.data ?? [];

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['credit-queue'] });
    qc.invalidateQueries({ queryKey: ['do'] });
    qc.invalidateQueries({ queryKey: ['enquiries'] });
  }

  const approveMutation = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) => approveCreditOverride(id, note),
    onSuccess: () => { invalidate(); toast.push('Credit override approved', 'success'); },
    onError: (e) => toast.push(apiError(e, 'Approve failed'), 'error'),
  });

  const blockMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => blockCredit(id, reason),
    onSuccess: () => { invalidate(); toast.push('Credit blocked', 'info'); },
    onError: (e) => toast.push(apiError(e, 'Block failed'), 'error'),
  });

  async function onApprove(d: DispatchOrder) {
    const note = await ask({ title: `Approve override for ${d.doNo}?`, label: 'Note for audit trail', multiline: true, confirmLabel: 'Approve' });
    if (!note?.trim()) return;
    approveMutation.mutate({ id: d.id, note });
  }

  async function onBlock(d: DispatchOrder) {
    const reason = await ask({ title: `Block ${d.doNo}?`, label: 'Reason', multiline: true, tone: 'danger', confirmLabel: 'Block' });
    if (!reason?.trim()) return;
    blockMutation.mutate({ id: d.id, reason });
  }

  return (
    <div>
      <SectionHeader title="Credit Check" />

      <div className="mb-2 flex justify-end">
        <button
          type="button"
          onClick={() => qc.invalidateQueries({ queryKey: ['credit-queue'] })}
          className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs"
        >
          <RefreshCw size={12} className={query.isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {query.isFetching && data.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center text-sm text-[var(--text-muted)]">
          Loading…
        </div>
      ) : data.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center text-sm text-[var(--text-muted)]">
          <PartyPopper size={16} className="mr-1 inline-block align-[-3px]" /> No DOs awaiting credit decision
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-4">
          {data.map((d) => {
            const limit = Number(d.party.creditLimit ?? 0);
            const outstanding = Number(d.party.outstanding ?? 0);
            const orderValue = Number(d.totalValue);
            const used = limit > 0 ? Math.round(((outstanding + orderValue) / limit) * 100) : 0;
            return (
              <div
                key={d.id}
                className="flex flex-col gap-3 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-5 backdrop-blur-xl"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-base font-black text-accent">{d.doNo}</div>
                    <div className="text-sm font-bold">{d.party.name}</div>
                    <div className="text-[11px] text-[var(--text-muted)]">
                      {orderRef(d.enquiry)} · {formatDate(d.doDate)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {d.creditStatus && (
                      <Badge tone={CREDIT_TONE[d.creditStatus]}>Credit {d.creditStatus}</Badge>
                    )}
                  </div>
                </div>

                {/* Credit numbers */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <Meta label="Credit Limit">{formatINRSymbol(limit)}</Meta>
                  <Meta label="Outstanding">{formatINRSymbol(outstanding)}</Meta>
                  <Meta label="This Order">{formatINRSymbol(orderValue)}</Meta>
                </div>
                <div className="rounded-md bg-white/[0.02] p-2 text-center text-xs">
                  After this order: <strong>₹{formatINR(outstanding + orderValue)}</strong> ={' '}
                  <strong
                    className={used > 90 ? 'text-danger' : used >= 70 ? 'text-warning' : 'text-success'}
                  >
                    {used}%
                  </strong>{' '}
                  of limit
                </div>

                {d.creditNote && (
                  <div className="rounded-md border border-[var(--border-color)] bg-white/[0.02] p-2 text-[11px] italic text-[var(--text-muted)]">
                    “{d.creditNote}”
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => onApprove(d)}
                    disabled={approveMutation.isPending}
                    icon={<Check size={14} />}
                  >
                    Approve Override
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => onBlock(d)}
                    disabled={blockMutation.isPending}
                    icon={<Ban size={14} />}
                  >
                    Block
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="text-sm font-bold text-[var(--text-primary)]">{children}</div>
    </div>
  );
}
